import { useMemo, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import {
  useBlokirTanggal, useBukaBlokir, useJadwalSaya, useKetersediaan,
} from '../../../domains/meeting/api'
import { tanggal, tanggalJam } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, Body, Gagal, Isian, Kalender, KepalaHalaman, Kolom2, Kosong, Memuat,
  Panel, Sub, Tab, Tabel, Tombol,
} from '../../../shared/ui'

function bulanIni() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function geserBulan(b, arah) {
  const [y, m] = b.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + arah, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const NAMA_BULAN = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

/**
 * KETERSEDIAAN SAYA — kalender yang bisa ditekan, mengikuti `desain/tenant-marketing.html`.
 *
 * Sebelumnya tab ini KOSONG di layar, dan tidak satu pun dari tiga sebabnya terlihat:
 *
 *   1. `useKetersediaan()` memanggil rutenya TANPA `month`, yang wajib -> 422
 *   2. balasannya objek `{ blocks, meeting_dates }`, dibaca sebagai larik -> `.length`
 *      undefined, jadi cabang "kosong" DAN cabang "ada isi" sama-sama tidak terpenuhi
 *   3. `useBlokirTanggal` sudah ada di domain dan tidak pernah dipanggil satu layar pun,
 *      jadi memblokir tanggal memang tidak mungkin dilakukan dari mana pun
 *
 * Rutenya sendiri lengkap sejak lama. Yang hilang cuma layarnya.
 */
// Diekspor supaya `uji_render.jsx` bisa merendernya langsung: renderToString cuma
// menghasilkan keadaan AWAL, dan tab bawaannya 'jadwal' — jadi kalender ini tidak
// akan pernah ikut terender lewat <Meeting /> saja.
export function Ketersediaan({ t, locale }) {
  const [bulan, setBulan] = useState(bulanIni)
  const [alasan, setAlasan] = useState('')
  const q = useKetersediaan(bulan)
  const blokir = useBlokirTanggal()
  const buka = useBukaBlokir()

  const blocks = q.data?.blocks ?? []
  const meetingDates = q.data?.meeting_dates ?? []
  const punyaSaya = blocks.filter((b) => b.mine)

  // Tanda kalender. Urutannya menentukan yang menang saat satu hari punya dua arti:
  // meeting dulu (fakta yang sudah disepakati), lalu blokir orang lain, lalu blokir saya.
  const tanda = useMemo(() => {
    const p = {}
    for (const d of meetingDates) p[d] = { nada: 'meeting', judul: t('meeting.adaMeeting') }
    for (const b of blocks) {
      if (!b.mine) p[b.blocked_date] = { nada: 'blokirTim', judul: t('meeting.blokirTim') }
    }
    for (const b of blocks) {
      if (b.mine) p[b.blocked_date] = { nada: 'blokirSaya', judul: b.reason || t('meeting.blokirSaya') }
    }
    return p
  }, [blocks, meetingDates, t])

  // Tanggal yang saya blokir TAPI sudah ada meeting terkonfirmasi di situ. Memblokir
  // tidak membatalkan meeting yang sudah disepakati — ia cuma menghentikan tawaran baru.
  const bentrok = punyaSaya.filter((b) => meetingDates.includes(b.blocked_date))

  const [y, m] = bulan.split('-').map(Number)

  function tekanHari(iso) {
    const milikSaya = punyaSaya.find((b) => b.blocked_date === iso)
    if (milikSaya) buka.mutate(milikSaya.id)
    else blokir.mutate({ blocked_date: iso, reason: alasan.trim() || null })
  }

  const galat = q.error || blokir.error || buka.error

  return (
    <Kolom2>
      <Panel
        judul={`${NAMA_BULAN[m - 1]} ${y}`}
        catatan={t('meeting.klikUntukBlokir')}
        aksi={
          <span className="flex gap-1">
            <Tombol kecil jenis="sekunder" onClick={() => setBulan(geserBulan(bulan, -1))}>‹</Tombol>
            <Tombol kecil jenis="sekunder" onClick={() => setBulan(geserBulan(bulan, 1))}>›</Tombol>
          </span>
        }
      >
        <Body className="flex flex-col gap-3">
          {q.isLoading && <Memuat tinggi={5} />}
          {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

          {q.data && (
            <>
              <Kalender
                bulan={bulan}
                tanda={tanda}
                sibuk={blokir.isPending || buka.isPending}
                onPilih={tekanHari}
              />

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink3">
                {[
                  ['bg-accent', t('meeting.blokirSaya')],
                  ['bg-warnSoft border border-warn', t('meeting.blokirTim')],
                  ['bg-okSoft border border-ok', t('meeting.adaMeeting')],
                ].map(([kelas, label]) => (
                  <span key={label} className="flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-[2px] ${kelas}`} />
                    {label}
                  </span>
                ))}
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
                  {t('meeting.alasan')}
                </span>
                <Isian
                  value={alasan}
                  placeholder="Cuti, training internal…"
                  onChange={(e) => setAlasan(e.target.value)}
                />
                <Sub>{t('meeting.alasanCatatan')}</Sub>
              </label>
            </>
          )}
        </Body>
      </Panel>

      <div className="flex flex-col gap-3.5">
        <Panel judul={t('meeting.diblokirHitung', { n: punyaSaya.length })}>
          {punyaSaya.length === 0 ? (
            <Kosong judul={t('meeting.blokirKosong')} keterangan={t('meeting.blokirKosongKet')} />
          ) : (
            <Tabel kepala={[t('meeting.kapan'), t('meeting.alasan'), '']}>
              {punyaSaya.map((b) => (
                <Baris key={b.id}>
                  <td className="font-mono text-[11.5px]">{tanggal(b.blocked_date, locale)}</td>
                  <td>{b.reason || <span className="text-ink3">—</span>}</td>
                  <td className="text-right">
                    <Tombol
                      jenis="sekunder"
                      kecil
                      disabled={buka.isPending}
                      onClick={() => buka.mutate(b.id)}
                    >
                      {t('meeting.bukaBlokir')}
                    </Tombol>
                  </td>
                </Baris>
              ))}
            </Tabel>
          )}
        </Panel>

        {bentrok.map((b) => (
          <Bendera
            key={b.id}
            nada="bad"
            judul={t('meeting.bentrokJudul', { tanggal: tanggal(b.blocked_date, locale) })}
          >
            <p>{t('meeting.bentrokIsi')}</p>
          </Bendera>
        ))}

        {galat && !q.isError && (
          <Bendera nada="bad" role="alert">{galat.message}</Bendera>
        )}

        <Bendera nada="warn" judul={t('meeting.hariKerjaJudul')}>
          <p>{t('meeting.hariKerjaIsi')}</p>
        </Bendera>
      </div>
    </Kolom2>
  )
}

export default function Meeting() {
  const { t, locale } = useBahasa()
  const [tab, setTab] = useState('jadwal')
  const jadwal = useJadwalSaya()

  return (
    <>
      <KepalaHalaman judul={t('meeting.judul')} keterangan={t('meeting.keterangan')} />

      <Tab
        aktif={tab}
        set={setTab}
        daftar={[
          { id: 'jadwal', label: t('meeting.jadwalSaya') },
          { id: 'ketersediaan', label: t('meeting.ketersediaan') },
        ]}
      />

      {tab === 'jadwal' && (
        <Panel judul={t('meeting.jadwalSaya')}>
          {jadwal.isLoading && <Memuat />}
          {jadwal.isError && <Gagal galat={jadwal.error} ulangi={jadwal.refetch} />}
          {jadwal.data && (jadwal.data.length ?? 0) === 0 && (
            <Kosong
              judul={t('meeting.kosong')}
              keterangan={t('meeting.kosongKet')}
            />
          )}
          {jadwal.data?.length > 0 && (
            <Tabel kepala={[t('meeting.kapan'), t('meeting.dengan'), t('meeting.bentuk'), t('meeting.bersama')]}>
              {jadwal.data.map((m, i) => (
                <Baris key={m.id ?? i}>
                  <td className="font-mono text-[11.5px]">{tanggalJam(m.confirmed_at ?? m.date, locale)}</td>
                  <td className="font-semibold">{m.company_name ?? '—'}</td>
                  <td>{m.meeting_format === 'online' ? t('prospek.online')
                    : m.meeting_format === 'offline' ? t('prospek.offline') : '—'}</td>
                  <td>{m.colleagues?.length ? m.colleagues.join(', ') : <span className="text-ink3">—</span>}</td>
                </Baris>
              ))}
            </Tabel>
          )}
        </Panel>
      )}

      {tab === 'ketersediaan' && <Ketersediaan t={t} locale={locale} />}
    </>
  )
}
